import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import QueryBuilder from './QueryBuilder';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import {LibraryAddRounded, AutorenewRounded} from "@mui/icons-material";
import { Row, Tabs, Segmented, Layout, Divider, Modal, Space } from 'antd';
import { fetchFilteredTickers } from "../../api/tickersApi";
import { MongoQueryBuilder } from "../../utils/mongoQuery";
import TickerTable from './TickerTable';
import {screenerMetrics } from "./screensConfig";
import PageHeader from "../../layouts/PageHeader";
import {iconMapping} from "../../configs/AppsConfig";
import {HorizontalRuleRounded, ChevronRightRounded, ChevronLeftRounded} from "@mui/icons-material";
import {getScreenTypeColor, BASE_PROJECTION_FIELDS} from "./screensConfig";
import AppDataForm from "../../SuperComponents/AppData/AppDataForm";
import {selectAppDataById, selectAppDataByType} from "../../store/slices/user.slice";
import {loadSectorsIndustries} from "../../store/slices/globalResources.slice";
import CategoricalFilters from "./CategoricalFilters";
import ScreenerToolbar from "./ScreenerToolbar";
import ScreenerCart from "./ScreenerCart";
const { TabPane } = Tabs;



// Update the buildMongoQuery function in Screens.jsx
const buildMongoQuery = ({securityType, filters, limit=100})=>{
    const queryBuilder = new MongoQueryBuilder();
    // Add the type condition
    queryBuilder.addCondition({ fullPath: 'type', operator: '=', value: securityType });

    // Add common projection and sorts for all queries
    queryBuilder.updateProjection([...BASE_PROJECTION_FIELDS]);
    queryBuilder.setSort({"metrics.mktcap": -1});

    if (Object.keys(filters).length > 0){
        for (let k in filters){
            if (k.startsWith('tags.')) {
                // Handle categorical filters with $in operator
                queryBuilder.customConditions.push({
                    [k]: { $in: Array.isArray(filters[k].value) ? filters[k].value : [filters[k].value] }
                });
            } else if (screenerMetrics[k]) {
                // Handle metric filters
                const metricObject = screenerMetrics[k];
                const mult = metricObject.type === 'percentage' ? 0.01: 1;
                let innerQuery = null;
                if (Array.isArray(filters[k].value)){
                    innerQuery = {
                        $gt: filters[k].value[0] * mult,
                        $lt: filters[k].value[1] * mult,
                    }
                } else {
                    innerQuery = {
                        [filters[k].op==='greater'? '$gt': '$lt']: filters[k].value * mult
                    }
                }
                queryBuilder.customConditions.push({[`metrics.${k}`]: innerQuery});
            }
        }

        // Add exchange filter for stocks only
        if (securityType === 'stock') {
            queryBuilder.customConditions.push({
                exchange: {$nin: ['pink', '', 'otcqb', 'otcbb', 'otcqx', 'otcgrey', 'otcce', 'otcmkts']},
            });
        }
    }
    queryBuilder.setLimit(limit);
    return queryBuilder;
}


const FilterTag = ({filter, handleRemoveFilter})=>{
    let Icon = HorizontalRuleRounded;
    if (filter.condition !== 'in') {
        Icon = filter.condition === 'greater' ? ChevronRightRounded : ChevronLeftRounded;
    }

    // Format the display name by removing 'tags.' prefix
    const displayName = filter.name.startsWith('tags.') ? filter.name.replace('tags.', '') : filter.name;

    // Format the value display
    const valueDisplay = Array.isArray(filter.value)
        ? `[${filter.value.length} selected]`
        : filter.value;

    // Use categorical color for tag filters, metric colors for others
    const background = filter.name.startsWith('tags.')
        ? getScreenTypeColor('categorical')
        : getScreenTypeColor(filter.name);

    return (
        <Chip
            sx={{
                background: background,
                color: '#FFF'
            }}
            icon={<Icon style={{color: "#FFF"}} />}
            label={`${displayName} ${valueDisplay}`}
            onDelete={() => handleRemoveFilter(filter.name)}
        />
    )
}
const ActiveFilters = ({filters, handleRemoveFilter})=>{
    if (!filters) return null;
    return (
        <>
            {Object.entries(filters).length > 0 && (
                <Row className={'mt-3 bg-transparent bordered rounded-sm'} style={{borderRadius: '8px', padding: '10px 20px'}} wrap>
                    {Object.entries(filters).map(([k, v])=>(
                        <div className={'p-2'}>
                            <FilterTag key={k} filter={{name: k, condition: v.condition, value: v.value}} handleRemoveFilter={handleRemoveFilter} />
                        </div>
                    ))}
                </Row>
            )}

        </>
    )
}


const SecurityTypeSegment = ({ value, onChange }) => {


  return (
    <ButtonGroup variant="outlined" aria-label="security type selection">
      <Button value="stock" variant={value==='stock'?'contained':'outlined'} onClick={()=>onChange('stock')}>Stocks</Button>
      <Button value="etf" variant={value==='etf'?'contained':'outlined'} onClick={()=>onChange('etf')}>ETFs</Button>
    </ButtonGroup>
  );
};


const Screens = () => {
  const [filters, setFilters] = useState({});
  const [activeKey, setActiveKey] = useState('stock');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const dispatch = useDispatch();
  const savedScreeners = useSelector((state)=>selectAppDataByType(state, 'screener'));
  const selectedScreenerId = useSelector((state)=>state.appStates.selectedAppdataIds.screen);
  const selectedScreener = useSelector((state)=>selectAppDataById(state, selectedScreenerId));
  const allFilteredTickers = useSelector(state => state.tickers.filteredTickers);
  const getMongoQuery=()=>{
      const mongoQuery = buildMongoQuery({securityType: activeKey, filters});
      return mongoQuery.toMongoQuery()
  }

  const tickerCategories = useSelector(state=>state.global.cache.sectorsIndustries);

  useEffect(()=>{
      dispatch(loadSectorsIndustries())
  }, [dispatch])  // Load sectors and industries on mount



  console.log(tickerCategories)

  const executeQuery = ()=>{
      dispatch(fetchFilteredTickers(getMongoQuery(), activeKey));
  }

  const formatPayload = ()=>{
      const query = getMongoQuery();
      return {
          filters: filters,
          query: JSON.stringify(query),
      }
  }


  useEffect(() => {
      executeQuery();
  }, [dispatch, activeKey]);


  const handleFilter = (filterName, op, value, enabled) => {
      if (enabled){
          setFilters(prev => {
              return {...prev, [filterName]: {op, value}}
          });
      }
      else {
          let newFilters = {...filters};
          delete newFilters[filterName];
          setFilters(newFilters);
      }
  };

  const onRemoveFilter = (filterName)=>{
      handleFilter(filterName, null, null, false);
  }



  return (
      <>
        <PageHeader title={'Screens'}
                    logoUrl={iconMapping.screener}
                    actions={[
                        <SecurityTypeSegment value={activeKey} onChange={(v)=>setActiveKey(v)} />,

                        <Button startIcon={<AutorenewRounded/>} variant={'outlined'} color={'primary'} onClick={executeQuery}>SUBMIT</Button>,
                        <Button onClick={()=>setShowSaveModal(true)} startIcon={<LibraryAddRounded/>} variant={'text'}>Save to Screens</Button>,
                    ]}
        />

        <Layout>
            <Layout.Sider className={'bg-transparent border-r border-gray-100 p-3'}
              width={350}
            >
                   <CategoricalFilters
                        securityType={activeKey}
                        onFilterChange={handleFilter}
                    />
                    <Divider />
                <QueryBuilder metrics={screenerMetrics} onAddFilter={handleFilter} />
            </Layout.Sider>
            <Layout.Content className={'px-8 py-3'}>
              <ActiveFilters handleSave={()=>setShowSaveModal(true)}
                             filters={filters}
                             handleRemoveFilter={onRemoveFilter}
              />
                <Divider/>
                {Array.isArray(allFilteredTickers) && allFilteredTickers.length > 0 && (
                    <TickerTable tickers={allFilteredTickers} selectable={true} />
                )}
            </Layout.Content>
            <ScreenerCart defaultType={activeKey} />

            {showSaveModal && (
                <AppDataForm appId={selectedScreenerId}
                             appType={'screener'}
                             initialData={formatPayload()}
                             initialTitle={'My New Screen'}
                             open={showSaveModal}
                             onClose={() => setShowSaveModal(false)}
                />
            )}

        </Layout>
      </>
  );
};

export default Screens;
